import { makeObservable, observable } from 'mobx';
import { ChatMessage } from './components/ChatMessage';
import { Attachments } from './components/Attachments';
import { History } from './components/History';
import { observer } from 'mobx-react-lite';
import { TextArea as TTT } from './components/TextArea';
import { Box, getVariant } from '@apia/theme';
import { AudioRecorder, getLabel } from '@apia/util';
import { CameraController } from './CameraController';
import { getBase64FromBlob, getBase64FromFile } from './util';
import { ChatAssistantTurn } from './components/AssistantTurnMessage';
import {
  ClearHistoryCallback,
  IAttachment,
  MessageCallbackProps,
  MessageSubmitCallback,
  TChatAssistantPanelConfig,
  TChatAssistantTurnSnapshot,
  TChatControllerState,
  TChatModelOption,
  TMessageType,
} from './types';
import { TMenuItem } from '@apia/components';
import type { ReactNode } from 'react';
import { TChatFeedbackForm } from './TChatFeedbackForm';

type TCreateAssistantTurnMessageProps = {
  attachments?: IAttachment[];
  feedbackForm?: TChatFeedbackForm;
  messageType?: TMessageType;
  panels?: TChatAssistantPanelConfig[];
  reference?: ReactNode;
  snapshot?: TChatAssistantTurnSnapshot;
};

type TChangeModelCallback = (model: string) => Promise<boolean> | boolean;

const CHAT_PLAN_BLOCK_TYPE = 'ai.plan';

type TChatControllerOptions = {
  feedbackForm: TChatFeedbackForm;
};

type TChatPlanItem = {
  description?: string;
  id: string;
  intent?: string;
  status: string;
  text: string;
  title?: string;
};

type TChatPlanState = {
  items: TChatPlanItem[];
  title?: string;
};

type TChatPlanStreamBlock = {
  description?: string;
  type?: string;
};

type TChatPlanPayload = {
  action?: string;
  items?: Array<Partial<TChatPlanItem> | null>;
  title?: string;
};

type TChatControllerInternalState<
  T extends Record<string, unknown>,
> = TChatControllerState &
  T & {
    feedbackForm: TChatFeedbackForm;
    plan: TChatPlanState | null;
  };

function parseChatPlanPayload(content?: string): TChatPlanPayload | null {
  if (!content?.trim()) {
    return null;
  }
  try {
    return JSON.parse(content) as TChatPlanPayload;
  } catch (error) {
    console.warn('Cannot parse chat plan payload', error);
    return null;
  }
}

function cleanChatPlanItem(
  item: Partial<TChatPlanItem> | null,
): TChatPlanItem | null {
  const id = String(item?.id ?? '').trim();
  const title = String(item?.title ?? '').trim();
  const intent = String(item?.intent ?? '').trim();
  const description = String(item?.description ?? '').trim();
  const fallbackText = title || intent || description;
  const text = String(item?.text ?? fallbackText).trim();
  const status = String(item?.status ?? '').trim();
  if (!id || !text) {
    return null;
  }
  return { description, id, intent, status, text, title };
}

const ChatPlanPanel = observer(({ plan }: { plan: TChatPlanState | null }) => {
  if (!plan?.items.length) {
    return null;
  }
  return (
    <Box className="chatPlan">
      <Box className="chatPlan__items">
        {plan.items.map((item) => {
          const done = item.status === 'done';
          const tooltip =
            item.title || item.intent || item.description || item.text;
          return (
            <Box
              className={`chatPlanItem ${
                done ? 'chatPlanItem--done' : 'chatPlanItem--pending'
              }`}
              key={item.id}
              title={tooltip}
            >
              <Box aria-hidden className="chatPlanItem__check" />
              <Box className="chatPlanItem__text" title={tooltip}>
                {item.text}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

function audioFileExtensionForMimeType(mimeType?: string) {
  const cleanMimeType = mimeType?.split(';')[0]?.toLowerCase();
  switch (cleanMimeType) {
    case 'audio/webm':
      return 'webm';
    case 'audio/ogg':
      return 'ogg';
    case 'audio/mp4':
      return 'mp4';
    case 'audio/mpeg':
      return 'mp3';
    case 'audio/wav':
    case 'audio/wave':
    case 'audio/x-wav':
      return 'wav';
    default:
      return 'webm';
  }
}

export class ChatController<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  currentIndex = 0;
  state: TChatControllerInternalState<T> = {
    isLoading: false,
    isStopping: false,
    isModelSelectionLoading: false,
    allowModelSelection: false,
    canRecord: true,
    canAttach: true,
    canOpenCamera: true,
    canNavigateHistory: true,
    feedbackForm: 'stack',
    modelOptions: [],
    messages: [],
    currentMessage: new ChatMessage('', 'user'),
    pendingMessages: [],
    pendingMessagesHeight: 0,
    plan: null,
  } as unknown as TChatControllerInternalState<T>;
  private internalAudioRecorder = new AudioRecorder();
  private internalCameraRecorder = new CameraController();
  private isSubmitting = false;
  private stopPromise?: Promise<unknown>;

  private maxId = 0;

  audioRecorder = {
    cancelOnStop: false,
    start: () => {
      this.state.isLoading = true;
      this.internalAudioRecorder
        .start()
        .then(async (blob: Blob) => {
          if (this.audioRecorder.cancelOnStop) {
            this.audioRecorder.cancelOnStop = false;
            return;
          }
          const extension = audioFileExtensionForMimeType(blob.type);
          this.state.currentMessage.attachments.push({
            id: String(this.maxId++),
            name: `userRecord_${this.audioRecorder.record++}.${extension}`,
            base64: await getBase64FromBlob(blob),
          });
          const currentMessage = new ChatMessage(
            this.state.currentMessage.message,
            'user',
          );
          this.state.currentMessage.message = '';
          const submitMessage = this.state.currentMessage;
          this.state.currentMessage = currentMessage;
          await this.handleSubmit({
            message: submitMessage,
            eraseCurrentMessage: false,
          });
        })
        .finally(() => {
          this.state.isLoading = false;
        });
    },
    stop: async () => {
      this.internalAudioRecorder.stop();
    },
    record: 0,
    state: this.internalAudioRecorder.state,
  };

  constructor(
    props: Partial<TChatControllerState & T & TChatControllerOptions>,
    private onMessageSubmit: MessageSubmitCallback,
    private onClearHistory: ClearHistoryCallback,
    private additionalOptions?: TMenuItem[],
    private classNames?: {
      history?: string;
      textarea?: string;
      attachments?: string;
    },
    private onStop?: () => Promise<unknown> | unknown,
    private onChangeModel?: TChangeModelCallback,
  ) {
    this.state = Object.assign({}, this.state, props);
    makeObservable(this, { state: observable });
  }

  removeMessage(idx: number) {
    this.state.messages = this.state.messages.filter((_, i) => i !== idx);
  }

  addMessage(message: ChatMessage) {
    this.state.messages.push(message);
    this.currentIndex = this.state.messages.length;
  }

  createAssistantTurnMessage({
    feedbackForm,
    panels = [],
    snapshot,
  }: TCreateAssistantTurnMessageProps = {}) {
    const turn = new ChatAssistantTurn({
      feedbackForm: feedbackForm ?? this.state.feedbackForm,
      panels,
      snapshot,
    });
    const message = turn.createPanelMessage();
    message.messageType = 'customMessage';

    return { message, turn };
  }

  startAssistantTurn(props: TCreateAssistantTurnMessageProps = {}) {
    const finalMessage = new ChatMessage(
      '',
      props.messageType ?? 'system',
      props.attachments ?? [],
      props.reference ?? null,
    );
    const result = {
      message: finalMessage,
      turn: new ChatAssistantTurn({
        controller: this,
        feedbackForm: props.feedbackForm ?? this.state.feedbackForm,
        finalMessage,
        panels: props.panels ?? [],
        snapshot: props.snapshot,
      }),
    };

    return result;
  }

  clearHistory() {
    this.state.messages = [];
    this.state.pendingMessages = [];
    this.state.plan = null;
    this.onClearHistory && this.onClearHistory();
  }

  clearMessage() {
    this.state.currentMessage = new ChatMessage('', 'user');
  }

  public async stop() {
    if (!this.state.isLoading) {
      return;
    }

    if (this.stopPromise) {
      await this.stopPromise;
      return;
    }

    this.state.isStopping = true;
    this.stopPromise = Promise.resolve(this.onStop?.())
      .catch(() => {
        this.state.isStopping = false;
      })
      .finally(() => {
        this.stopPromise = undefined;
      });
    await this.stopPromise;
  }

  public removeAttachment(attachment: IAttachment) {
    this.state.currentMessage.removeAttachment(attachment);
  }

  public removePendingMessage(message: ChatMessage) {
    this.state.pendingMessages = this.state.pendingMessages.filter(
      (current) => current !== message,
    );
  }

  public updatePendingMessage(message: ChatMessage, value: string) {
    const pendingMessage = this.state.pendingMessages.find(
      (current) => current === message,
    );
    if (pendingMessage) {
      pendingMessage.message = value;
    }
  }

  public setModelOptions(
    modelOptions: TChatModelOption[],
    selectedModel?: string,
  ) {
    this.state.modelOptions = modelOptions;
    if (selectedModel !== undefined) {
      this.state.selectedModel = selectedModel;
    } else if (
      this.state.selectedModel &&
      !modelOptions.some((model) => model.id === this.state.selectedModel)
    ) {
      this.state.selectedModel = modelOptions[0]?.id;
    }
  }

  public async changeModel(model: string) {
    if (
      !model ||
      model === this.state.selectedModel ||
      this.state.isModelSelectionLoading
    ) {
      return;
    }

    this.state.isModelSelectionLoading = true;
    try {
      const changed = await Promise.resolve(this.onChangeModel?.(model) ?? true);
      if (changed) {
        this.state.selectedModel = model;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.state.isModelSelectionLoading = false;
    }
  }

  private hasMessageContent(message: ChatMessage) {
    return Boolean(message.message || message.attachments.length > 0);
  }

  private queuePendingMessage(message: ChatMessage) {
    if (!this.hasMessageContent(message)) {
      return;
    }
    this.state.pendingMessages.push(message);
  }

  private shiftPendingMessage() {
    let nextMessage: ChatMessage | undefined;
    let remainingMessages = this.state.pendingMessages;

    while (remainingMessages.length > 0 && !nextMessage) {
      const [candidate, ...rest] = remainingMessages;
      remainingMessages = rest;
      if (this.hasMessageContent(candidate)) {
        nextMessage = candidate;
      }
    }

    this.state.pendingMessages = remainingMessages;
    return nextMessage;
  }

  private async submitMessage(message: ChatMessage) {
    if (!this.hasMessageContent(message)) {
      return;
    }

    if (this.isSubmitting) {
      this.queuePendingMessage(message);
      return;
    }

    this.isSubmitting = true;
    this.state.isLoading = true;
    document.dispatchEvent(new Event('enableAutoScroll'));
    try {
      await this.onMessageSubmit({
        message,
        model: this.state.selectedModel,
        ...this.getAdditionalProps(),
      });
    } finally {
      if (this.stopPromise) {
        await this.stopPromise;
      }
      this.isSubmitting = false;
      const nextMessage = this.shiftPendingMessage();
      if (nextMessage) {
        await this.submitMessage(nextMessage);
      } else {
        this.state.isLoading = false;
        this.state.isStopping = false;
      }
    }
  }

  private handleSubmit = async (props?: {
    /**
     * If provided, this message will be sumitted instead of the current message.
     */
    message?: ChatMessage;
    /**
     * Defaults to true
     */
    eraseCurrentMessage?: boolean;
  }) => {
    if (this.audioRecorder.state.recording) {
      await this.audioRecorder.stop();
      return;
    }

    if (this.state.isModelSelectionLoading) {
      return;
    }
    const currentMessage = props?.message || this.state.currentMessage;
    if (this.hasMessageContent(currentMessage)) {
      if (props?.eraseCurrentMessage !== false) {
        this.state.currentMessage = new ChatMessage('', 'user');
      }
      await this.submitMessage(currentMessage);
    }
  };

  private isForHistory(message?: ChatMessage) {
    return message && message.messageType === 'user';
  }

  private canGoNext() {
    let condition = false;
    const slice = this.state.messages.slice(
      this.currentIndex,
      this.state.messages.length,
    );
    const n = slice.length;
    if (this.currentIndex + 1 > n) {
      return condition;
    }
    for (let i = 0; i < n && !condition; i++) {
      const current = slice[i];
      condition = current.messageType === 'user';
    }
    return condition;
  }
  private canGoPrev() {
    let condition = false;
    if (this.currentIndex - 1 === -1) {
      return condition;
    }
    for (
      let i = this.state.messages.slice(0, this.currentIndex).length - 1;
      i >= 0 && !condition;
      i--
    ) {
      const current = this.state.messages[i];
      condition = current.messageType === 'user';
    }
    return condition;
  }
  public nextMessage() {
    let newCurrentMessage = undefined;
    let i = this.currentIndex;
    const n = this.state.messages.length;
    if (n === 0 || n - 1 === i) {
      return;
    }
    while (!newCurrentMessage && i < n - 1) {
      newCurrentMessage = this.state.messages[i + 1];
      i++;
      if (this.isForHistory(newCurrentMessage)) {
        this.state.currentMessage = newCurrentMessage!.clone();
        break;
      } else {
        newCurrentMessage = undefined;
      }
      if (i === this.currentIndex) {
        break;
      }
    }
    this.currentIndex = i;
  }

  public prevMessage() {
    let newCurrentMessage = undefined;
    let i = this.currentIndex;
    const n = this.state.messages.length;
    if (n === 0 || i === 0) {
      return;
    }
    while (!newCurrentMessage && i > 0) {
      newCurrentMessage = this.state.messages[i - 1];
      i--;
      if (this.isForHistory(newCurrentMessage)) {
        this.state.currentMessage = newCurrentMessage!.clone();
        break;
      } else {
        newCurrentMessage = undefined;
      }
      if (i === this.currentIndex) {
        break;
      }
    }
    this.currentIndex = i;
  }

  protected getAdditionalProps(): Partial<MessageCallbackProps> {
    return {};
  }

  public applyPlanStreamBlock(block: TChatPlanStreamBlock) {
    if (block.type !== CHAT_PLAN_BLOCK_TYPE) {
      return false;
    }
    const payload = parseChatPlanPayload(block.description);
    if (!payload || payload.action === 'close') {
      this.state.plan = null;
      return true;
    }
    const items = (payload.items ?? [])
      .map(cleanChatPlanItem)
      .filter((item): item is TChatPlanItem => item !== null);
    this.state.plan = {
      items,
      title: payload.title,
    };
    return true;
  }

  History = observer(() => (
    <History
      messages={this.state.messages}
      bottomInset={this.state.pendingMessagesHeight}
      className={this.classNames?.history}
    ></History>
  ));

  Attachments = observer(() => (
    <Attachments
      attachments={this.state.currentMessage.attachments}
      onRemove={(a) => {
        this.removeAttachment(a);
      }}
      className={this.classNames?.attachments}
      onAddDescription={(a, ev) =>
        this.state.currentMessage.addAttachmentDescription(a, ev)
      }
    ></Attachments>
  ));

  Plan = observer(() => <ChatPlanPanel plan={this.state.plan} />);

  TextArea = observer(() => (
    <TTT
      additionalOptions={this.additionalOptions}
      canAttach={this.state.canAttach !== false}
      allowModelSelection={this.state.allowModelSelection === true}
      canNavigateHistory={this.state.canNavigateHistory !== false}
      canOpenCamera={this.state.canOpenCamera !== false}
      canRecord={this.state.canRecord !== false}
      className={this.classNames?.textarea}
      value={this.state.currentMessage.message as string}
      isLoading={this.state.isLoading}
      isStopping={this.state.isStopping}
      isModelSelectionLoading={this.state.isModelSelectionLoading}
      isRecording={this.internalAudioRecorder.state.recording}
      modelOptions={this.state.modelOptions}
      selectedModel={this.state.selectedModel}
      disablePrevButton={!this.canGoPrev()}
      disableNextButton={!this.canGoNext()}
      onChange={(ev) => {
        this.state.currentMessage.message = ev;
      }}
      onChangeModel={(model) => {
        void this.changeModel(model);
      }}
      onSubmit={() => this.handleSubmit()}
      onStop={this.onStop ? () => this.stop() : undefined}
      onClearHistory={() => {
        this.clearHistory();
      }}
      onPendingMessagesHeightChange={(height) => {
        if (this.state.pendingMessagesHeight !== height) {
          this.state.pendingMessagesHeight = height;
        }
      }}
      onRemovePendingMessage={(message) => {
        this.removePendingMessage(message);
      }}
      onUpdatePendingMessage={(message, value) => {
        this.updatePendingMessage(message, value);
      }}
      onAttach={async (ev) => {
        if (ev) {
          if (
            !this.state.maxAttachmentsSize ||
            this.computeBase64Size(ev.base64) <= this.state.maxAttachmentsSize
          ) {
            this.state.currentMessage.attachments.push({
              ...ev,
              id: String(this.maxId++),
            });
          } else {
            this.addMessage(
              new ChatMessage(
                getLabel('msgErrorFileMaxSize', {
                  text: { TOK1: `${String(this.state.maxAttachmentsSize)}MB` },
                  title: { TOK1: `${String(this.state.maxAttachmentsSize)}MB` },
                }).text,
                'information',
              ),
            );
          }
        }
      }}
      onRecord={() => {
        !this.internalAudioRecorder.state.recording
          ? this.audioRecorder.start()
          : this.audioRecorder.stop();
      }}
      onRecordCancel={async () => {
        if (this.internalAudioRecorder.state.recording) {
          this.audioRecorder.cancelOnStop = true;
          await this.audioRecorder.stop();
        }
      }}
      onVideoRecord={async () => {
        !this.internalCameraRecorder.state.isOpen &&
          (await this.internalCameraRecorder.openCamera());
        await new Promise<void>((resolve, _) => {
          const checkCapture = async () => {
            if (!this.internalCameraRecorder.state.isOpen) {
              const capture = this.internalCameraRecorder.pop();
              if (capture) {
                this.state.currentMessage.attachments.push({
                  id: String(this.maxId++),
                  name: capture.name,
                  base64: await getBase64FromFile(capture),
                });
                return resolve();
              }
            } else {
              requestAnimationFrame(checkCapture);
            }
          };

          checkCapture();
        });
      }}
      onPrev={() => {
        this.prevMessage();
      }}
      onNext={() => {
        this.nextMessage();
      }}
      attachments={this.state.currentMessage.attachments}
      pendingMessages={this.state.pendingMessages}
    />
  ));

  Renderer = () => (
    <Box
      {...getVariant('layout.common.chat.controller')}
      className="chat_controller"
    >
      <this.History />
      <this.Plan />
      <this.Attachments />
      <this.TextArea />
    </Box>
  );

  public computeBase64Size(base64: string) {
    base64 = base64.includes(',') ? base64.split(',').pop()! : base64;
    base64 = base64.replace(/\s/g, '');
    const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0;
    const bytes = (base64.length * 3 - padding * 4) / 4;
    return bytes / (1024 * 1024);
  }
}
